<template>
    <product-list-item-wrapper
        :route="route"
        :go-message="$t('backup view details button')"
        wrap-class="grid min-w-0 grid-cols-12 gap-x-1 text-xs sm:text-sm md:text-xs lg:gap-x-12 lg:text-sm"
    >
        <div class="col-span-5 flex flex-col lg:col-span-4 xl:flex-row xl:space-x-2 2xl:space-x-6">
            <div
                class="flex flex-1 shrink-0 items-center truncate font-medium text-gray-500"
                :class="{
                    'gap-y-1 sm:flex-col sm:items-start sm:justify-center': hasNameAccess,
                }"
            >
                <span class="px-2 sm:mt-1">
                    {{ $t('backup number') }}: {{ backup.id }}
                </span>
                <item-name
                    v-if="hasNameAccess"
                    :name="name"
                    @rename="rename"
                />
            </div>
            <div class="my-auto shrink-0 py-1">
                <crm-logo-link
                    v-if="backup.source.platform?.type"
                    class="flex-1 overflow-hidden"
                    :crm="backup.source.platform.type"
                    :name="backup.source.platform.name"
                    :url="backup.source.url"
                    logo-class="h-6 sm:h-10"
                />
            </div>
        </div>
        <div class="col-span-5 flex items-center lg:col-span-6">
            <product-list-item-info
                class="grid gap-2 text-gray-500 lg:grid-cols-2 lg:gap-4"
                :list="infoItems"
            />
        </div>

        <div class="col-span-2 flex items-center justify-center gap-x-6">
            <div v-if="showRun">
                <base-button
                    class="hidden sm:block"
                    size="small"
                    round
                    :disabled="disableRun"
                    @click="run"
                >
                    Run: {{ runs.valueUsed }}/{{ runs.value }}
                </base-button>
                <base-button
                    class="sm:hidden"
                    link
                    :icon="{ name: 'redo-alt' }"
                    :disabled="disableRun"
                    @click="run"
                />
            </div>
        </div>
    </product-list-item-wrapper>
</template>

<script>
import { get } from 'lodash-es';
import { mapActions, mapGetters } from 'vuex';
import { BACKUP } from '@/store/modules/acl-access/constants.ts';
import notify from '@/components/base-components/components/notification/notify';
import { backupInstanceCreate } from '@/components/backup/api';
import { CONNECTION as CONNECTION_MODULE } from '@/modules/list';
import { getAppModuleService } from '@/modules/registry';
import ProductListItemWrapper from '@/components/app/product/list/item-wrapper';
import ProductListItemInfo from '@/components/app/product/list/item/info/index.vue';
import CrmLogoLink from '@/components/crm-logo-link';
import ItemName from '@/components/app/product/item/item-name.vue';
import formatBytes from '@/utils/format-bytes.ts';

export default {
    name: 'BackupListItem',
    components: {
        ItemName,
        ProductListItemWrapper,
        ProductListItemInfo,
        CrmLogoLink,
    },
    props: {
        backup: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapGetters({
            isGranted: 'aclAccess/isGranted',
        }),
        hasNameAccess() {
            return this.isGranted(BACKUP.NAME);
        },
        route() {
            return {
                name: 'backup',
                params: {
                    backupId: this.backup.id,
                },
            };
        },
        name() {
            return this.backup.source.name || this.backup.name;
        },
        plan() {
            return this.backup.plan.current;
        },
        runs() {
            return get(this.plan, 'options.backup.features.BackupRun.data');
        },
        showRun() {
            return this.runs.value && this.runs.valueUsed;
        },
        availableRuns() {
            return this.runs.value - this.runs.valueUsed;
        },
        disableRun() {
            return this.availableRuns === 0 || this.hasRunningInstance;
        },
        hasRunningInstance() {
            return !!this.backup.instance?.isRunning;
        },
        demoPlan() {
            return this.plan.type === 'default';
        },
        planExpired() {
            return this.plan.status === 'expired';
        },
        planLabel() {
            if (this.planExpired) return this.$t('plan expired');

            return this.demoPlan ? this.$t('plan free') : this.$t('plan premium');
        },
        planClass() {
            if (this.planExpired) return 'text-red-500';

            return this.demoPlan ? 'text-amber-500' : 'text-emerald-500';
        },
        type() {
            return get(this.runs, 'scope') === 'demo' ? 'demo' : 'full';
        },
        infoItems() {
            return [
                {
                    condition: true,
                    icon: {
                        name: 'award',
                        class: this.planClass,
                    },
                    value: this.planLabel,
                    class: 'capitalize',
                },
                {
                    condition: this.backup.size,
                    icon: {
                        name: 'database',
                        class: 'text-gray-300',
                    },
                    label: this.$t('backup size'),
                    value: formatBytes(this.backup.size),
                },
                {
                    condition: this.backup.instances.type === 'demo',
                    icon: {
                        name: 'eye',
                        class: 'text-gray-300',
                    },
                    label: this.$t('backup samples'),
                    value: this.backup.instances.type,
                },
                {
                    condition: this.backup.instances.type === 'full',
                    icon: {
                        name: 'tachometer-alt',
                        class: 'text-gray-300',
                    },
                    label: this.$t('backup fulls'),
                    value: this.backup.instances.type,
                },
            ].filter(item => item.condition);
        },
    },
    methods: {
        ...mapActions({
            showLoader: 'loader/SHOW',
            hideLoader: 'loader/HIDE',
        }),
        async run() {
            await this.showLoader();

            try {
                await backupInstanceCreate(this.backup.id, this.type);

                await this.$router.push(this.route);
            } catch ({ error, message }) {
                notify.error({
                    title: this.$t('request error notification'),
                    message,
                    duration: 7500,
                });

                throw error;
            } finally {
                await this.hideLoader();
            }
        },
        async rename(name) {
            await this.showLoader();

            const oldName = this.name;

            this.$set(this.backup.source, 'name', name);

            try {
                await getAppModuleService(CONNECTION_MODULE)?.rename(this.backup.source.id, name);
            } catch ({ message }) {
                this.$set(this.backup.source, 'name', oldName);

                notify.error({
                    title: this.$t('request error notification'),
                    message,
                });
            } finally {
                await this.hideLoader();
            }
        },
    },
};
</script>
